<template>
    <div>
        <div v-if="currentPlanFrequency !== 'monthly'">
            <h2 class="pause--heading" style="text-align: center" v-text="$t('PAUSE_UPDATE_YOUR_PLAN')"></h2>
            <h6>{{ $t('PAUSE_CHANGE_FREQUENCY') }}</h6>
            <p class="pause-question" v-text="$t('PAUSE_DELIVERIES_RESTART_FREQUENCY')"></p>
            <radio-field
                v-for="(item, index) in plans"
                :key="index"
                v-model="selectedPlan"
                :option="item.plan_slug"
                :label="item.plan_name"/>

            <div class="divider"/>
        </div>

        <h6>{{ $t('PAUSE_SKIP_UNTIL') }}</h6>
        <p class="pause-question" v-text="$t('PAUSE_DELIVERIES_RESTART_DATE_QUESTION')"></p>

        <p class="pause-question">{{ $t('PAUSE_SELECT_DATE_TO_RESUME') }}</p>
        <Calendar
            class="calendar-container"
            :customStyle=customStyle
            :deliverable-days="allDeliveries"
            :controlLimit="10"
            :lockTooltipText="$t('CALENDAR_TOO_LATE_TO_MAKE_CHANGES')"
            :isPauseFlow="true"
            :allowMonthToggling="true"/>

        <button @click.prevent="pauseWithRestartDate" class="btn btn--primary btn--full-width restart-date--confirm">
            <span v-if="!processing" v-text="$t('PAUSE_DELIVERIES_RESTART_DATE_CONFIRM')"/>
            <i v-else class="btn__loading-icon fa-spin"/>
        </button>
        <button v-text="continueToPauseText"
                @click.prevent="setStage" class="btn btn--secondary-black btn--full-width restart-date--reject">
        </button>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Calendar } from '@freddiesflowers/frontend-component-library';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import SubscriptionService from '@/services/subscriptions/SubscriptionService';
import { CSPageEvents } from '@/helpers/GAHelpers';
import RadioField from '@/components/forms/fields/RadioField';

let subscriptionService = new SubscriptionService();

export default {
    name: 'RestartStep',
    components: { Calendar, RadioField },
    props: {
        subscriptionId: {
            required: true,
            type: Number,
        },
        pauseSurveyAnswer: {
            type: Object,
            required: false,
        },
        availableRestartDates: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            stageId: 'restart',
            isNextStepClicked: false,
            selectedRestartDate: null,
            errorHandler: new ErrorResponseHandler(),
            processing: false,
            selectedPlan: null,
            schedulePlanChange: true,
        };
    },
    mounted() {
        this.$root.$on('selected-date', (payload) => {
            this.selectedRestartDate = payload;
        });
        this.selectedPlan = this.plans.filter(item => item.plan_slug === this.currentPlanSlug)[0].plan_slug;
    },
    computed: {
        ...mapGetters({
            allDeliveries: 'scheduledOrder/getAllDeliveries',
            subscription: 'subscription/getActiveSubscription',
            plans: 'subscription/getPlans',
            deliveries: 'scheduledOrder/getDeliveries',
            isPauseFlow: 'account/getIsPauseFlow',
        }),
        updatedSteps() {
            const currentStepIndex = this.pauseSurveyAnswer.nextSteps.indexOf(this.stageId);
            return this.pauseSurveyAnswer.nextSteps.slice(currentStepIndex + 1);
        },
        currentPlanSlug() {
            return this.subscription && this.subscription.plan_slug;
        },
        customStyle() {
            return {
                container: {
                    width: '360px',
                },
                dayWrapper: {
                    height: '49px',
                    width: '52px',
                },
                dayText: {
                    height: '35px',
                    width: '35px',
                    fontSize: '16px',
                },
            };
        },
        continueToPauseText() {
            return this.isPauseFlow ? this.$t('PAUSE_DELIVERIES_NO_THANKS') : this.$t('CANCEL_DELIVERIES_NO_THANKS');
        },
        allowedDeliveries() {
            return this.deliveries.filter((item) => !item.is_locked && !item.scheduled_order_id)
                .map((item) => {
                    item.human = formatDate(new Date(item.datetime), Dates.DAY_DATE_MONTH_YEAR);
                    return item;
                });
        },
        subscriptionDetails() {
            return this.$store.getters['subscription/getSubscriptionById'](this.subscriptionId);
        },
        currentPlanFrequency() {
            return this.subscriptionDetails?.plan_slug;
        },
    },
    methods: {
        pauseWithRestartDate() {
            CSPageEvents(`${this.stageId}-accepted`);
            this.processing = true;
            if (!this.selectedRestartDate) return this.changeFrequency();
            let payload = {
                reason: 'Restart Step',
                feedback: null,
                resumeDate: this.selectedRestartDate,
                planSlug: this.selectedPlan,
            };
            subscriptionService.pauseSubscription(this.subscriptionId, payload)
                .then(() => {
                    this.$emit('deliveries-paused');
                    this.processing = false;
                })
                .catch(error => this.errorHandler.setResponse(error.response.data));
        },

        closePauseDeliveries() {
            this.stage = 1;
            this.$emit('closed');
        },

        changeFrequency() {
            subscriptionService.updateSubscriptionPlan(this.subscriptionId, this.selectedPlan, this.allowedDeliveries[0].datetime, this.schedulePlanChange)
                .then(() => {
                    this.$emit('subscription-updated');
                    this.$store.dispatch('subscription/fetchAllSubscriptions', true);
                    this.$store.dispatch('scheduledOrder/fetchAllDeliveries', true);
                }).catch(error => console.log(error.response.data));
            CSPageEvents(`${this.stageId}-paused`);
        },

        setStage() {
            this.$emit('updateStage', 'pause-reason');
        },
    },
};
</script>
<style lang="scss" scoped>
.pause-question {
    margin: 15px 0;
    text-align: left;
}
.restart-dates {
    margin-bottom: 20px;
}
.restart-date {
    margin: 8px 0;
}
.restart-date--label {
    font-size: 18px;
}
.restart-date--confirm {
    margin: 54px 0 0;
    background: #262323;
}
.restart-date--reject {
    margin: 10px 0 0px;
}

.divider {
    border: 1px solid #CECECE;
    margin: 20px 20px;
    position: relative;
}

.divider-text {
    position: absolute;
    z-index: 9999;
    padding: 10px;
    background-color: white;
    top: -30px;
    left: 44%;
}
</style>
